import BrandLogo from "../components/brand_logo.jsx";
import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Tag,
  Settings,
  HeartPulse,
  Building2,
  Share2,
  MessageSquare,
  Banknote,
  UserX,
  MapPin,
  TrendingUp,
  Clock,
} from "lucide-react";
import apiClient from "../api/client.js";
import { useAuth } from "../auth/auth_context.jsx";
import { canAccessPath, canViewFinancials } from "../auth/roles.js";

// Nav grouped to echo the Sales HQ design's sectioned sidebar while only
// linking routes that actually exist today.
const NAV_GROUPS = [
  {
    label: "Overview",
    items: [{ to: "/dashboard", label: "Dashboard", icon: LayoutDashboard }],
  },
  {
    label: "Revenue",
    items: [
      // Telnyx wallet deposits vs. what Telnyx bills us. Not a manager path, so
      // canAccessPath hides it from managers along with the rest of revenue.
      { to: "/forecasting", label: "Forecasting", icon: TrendingUp },
      { to: "/geography", label: "Geography", icon: MapPin },
      { to: "/messaging-economics", label: "Messaging & Phone", icon: MessageSquare },
    ],
  },
  {
    label: "Customers",
    items: [
      // Customer list + subscriptions in one place (CustomerHub).
      { to: "/customers", label: "Customers", icon: Users },
      { to: "/customer-health", label: "Customer Health", icon: HeartPulse },
      { to: "/trials", label: "Trials", icon: Clock },
      // Trials and Deactivations are open to customer_success but not to
      // managers; canAccessPath hides them per role (the API refuses too).
      // Deactivations' Reporting tab is admin-only; Billing health is hidden
      // from managers.
      { to: "/deactivations", label: "Deactivations", icon: UserX },
    ],
  },
  {
    // The brands and partners ScheduleDrop pays: who they are, the terms they
    // earn on, and what has actually been paid to them.
    label: "Referrers",
    items: [
      { to: "/franchisors", label: "Franchisors", icon: Building2 },
      // Commission terms for referral partners. Its own item rather than a section
      // of Settings: these rows decide what partners get paid.
      { to: "/referral-campaigns", label: "Referral Campaigns", icon: Share2 },
      // Payouts recorded by hand, which the franchisor portal reconciles against
      // what it calculated was owed.
      { to: "/referral-payments", label: "Referral Payments", icon: Banknote },
    ],
  },
  {
    label: "Operations",
    items: [
      { to: "/coupons", label: "Coupons", icon: Tag },
      { to: "/settings", label: "Settings", icon: Settings },
    ],
  },
];

function NavItem({ to, label, icon: Icon }) {
  const { pathname } = useLocation();
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        [
          "group flex h-8 items-center gap-2.5 border-l-2 pl-3 pr-2.5 text-[13px] transition-colors",
          (isActive || (to === "/customers" && pathname.startsWith("/subscriptions/")))
            ? "border-[#349ef7] bg-[#60A5FA]/12 font-semibold text-white"
            : "border-transparent font-medium text-[#E8EFF7]/78 hover:bg-white/[0.04] hover:text-white",
        ].join(" ")
      }
    >
      <Icon size={16} className="shrink-0" />
      <span>{label}</span>
    </NavLink>
  );
}

/**
 * Live MRR + active-subscribers pill at the top of the sidebar (per the Sales HQ
 * design). Pulls the point-in-time snapshot from the subscription-kpis report;
 * `mrr_cents` / `active_subscriptions` there are current values, independent of
 * the period window, so a rolling 30d window is just to satisfy the endpoint.
 */
function LiveMrrPill() {
  const [kpi, setKpi] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const to = new Date();
    const from = new Date(to.getTime() - 30 * 24 * 60 * 60 * 1000);
    apiClient
      .get("/api/reports/subscription-kpis", {
        params: { period_from: from.toISOString(), period_to: to.toISOString() },
        signal: controller.signal,
      })
      .then((res) => setKpi(res.data))
      .catch((err) => {
        if (err.name === "CanceledError" || err.code === "ERR_CANCELED") return;
        setError(true);
      });
    return () => controller.abort();
  }, []);

  const mrrDisplay =
    kpi?.mrr_cents != null
      ? `$${Math.round(kpi.mrr_cents / 100).toLocaleString("en-US")}`
      : error
      ? "—"
      : "…";
  const activeCount = kpi?.active_subscriptions;

  return (
    <div className="border-b border-white/10 px-4 py-3.5">
      <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#E8EFF7]/68">
        Live MRR
      </div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="tabular text-[22px] font-bold leading-none tracking-[-0.02em] text-white">
          {mrrDisplay}
        </span>
        <span className="live-dot text-[10px] font-semibold text-[#34D399]">LIVE</span>
      </div>
      <div className="mt-1 text-[10px] text-[#E8EFF7]/42">
        {activeCount != null
          ? `${activeCount.toLocaleString("en-US")} active subs`
          : error
          ? "Metrics unavailable"
          : "Loading metrics…"}
      </div>
    </div>
  );
}

export default function Sidebar() {
  const { user } = useAuth();
  // Only the links this role may open; groups left empty are dropped.
  const groups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => canAccessPath(user, item.to)),
  })).filter((group) => group.items.length > 0);

  return (
    <aside className="flex h-[100dvh] w-[232px] shrink-0 flex-col overflow-y-auto border-r border-white/10 bg-[#061122] text-white">
      {/* Logo */}
      <div className="flex items-center gap-2.5 border-b border-white/10 px-4 py-3.5">
        <BrandLogo className="h-9 w-9 shrink-0 text-white" />
        <div className="flex flex-col leading-tight">
          <span className="text-[13px] font-bold tracking-[-0.01em] text-white">
            ScheduleDrop
          </span>
          <span className="text-[10px] uppercase tracking-[0.08em] text-[#E8EFF7]/68">
            Sales HQ
          </span>
        </div>
      </div>

      {/* Live MRR pill — not rendered (and its reports call never made) for
          roles that may not see revenue. */}
      {canViewFinancials(user) ? <LiveMrrPill /> : null}

      <nav className="flex-1 overflow-y-auto px-2 py-2.5">
        {groups.map((group) => (
          <div key={group.label} className="mb-3.5">
            <div className="px-2.5 pb-1.5 pt-1 text-[10px] font-bold uppercase tracking-[0.10em] text-[#E8EFF7]/40">
              {group.label}
            </div>
            <ul className="space-y-px">
              {group.items.map((item) => (
                <li key={item.to}>
                  <NavItem {...item} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
    </aside>
  );
}
